import * as Parse from "papaparse";
import moment from "moment";
import usePrivatePlacementStore, { PrivatePlacement } from "../../stores/private-placements.store.ts";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import SplineApi from "../../tools/spline-api.js";

export enum SignedFiles {
    NATIONAL_PRIVATE_PLACEMENTS = "national_private_placements"
}


export default function usePrivatePlacements() {
  const { placements, setPlacements, setLoading } = usePrivatePlacementStore();
  const { getAccessTokenSilently } = useAuth0();

  /**
   * Fetches the private placements
   */
  async function fetchPrivatePlacements(): Promise<PrivatePlacement[]> {
    const accessToken = await getAccessTokenSilently();
    const splineApi = new SplineApi(accessToken);

    const csvUrl = await splineApi.getSignedUrl(SignedFiles.NATIONAL_PRIVATE_PLACEMENTS);

    const res = await axios.get(csvUrl, {
      headers: { "Content-Type": "text/csv;charset=UTF-8" },
      responseType: "text",
    });

    const csvData = Parse.parse(res.data, {}).data.slice(1, -1);

    return csvData.map((row, index) => {
      const [ 
        state, issuer, issue, finalMaturity, parAmount, datedDate, 
        _proxyPricingDate, type, taxDesignation, securityCode, proxyRating,
        proxyAvgLife, coupon, purchaser, advisor, bondCounsel, sourceFile,
        ratingNote, forward, draw, callDate, postDate
      ] = row;

      return {
        id: index + 1,
        row: row,
        state, 
        issuer, 
        issue, 
        finalMaturity: moment(finalMaturity, "M/D/YYYY"), 
        parAmount: parAmount === "" ? 0 : parseInt(parAmount.replaceAll(",", "")),
        datedDate: moment(datedDate, "M/D/YYYY"), 
        proxyPricingDate: moment(datedDate, "M/D/YYYY"), 
        type, 
        taxDesignation, 
        securityCode, 
        proxyRating,
        proxyAvgLife, 
        coupon,
        purchaser, 
        advisor, 
        bondCounsel, 
        sourceFile,
        ratingNote, 
        forward, 
        draw, 
        callDate: moment(callDate, "M/D/YYYY"), 
        postDate: moment(postDate, "M/D/YYYY"),
        tenor: moment(finalMaturity, "M/D/YYYY").diff(moment(datedDate, "M/D/YYYY"), "years", true)
      }
    });
  }
  
  /**
   * Returns the private placements.
   * 
   * @remarkss It only fetched the data once and caches the result.
   */
  async function getPrivatePlacements(): Promise<PrivatePlacement[]> {
    return loadingWrapper(async () => {
      if (placements.length !== 0) return placements;
      const result = await fetchPrivatePlacements();
      setPlacements(result);
      return result;
    });
  }

  /**
   * Reloads the private placements.
   */
  async function reloadPrivatePlacements() {
    return loadingWrapper(async () => {
      const result = await fetchPrivatePlacements();
      setPlacements(result);
    });
  }

  async function loadingWrapper<T>(method: () => Promise<T>): Promise<T> {
    setLoading(true);
    try {
      return await method();
    } finally {
      setLoading(false);
    }
  }

  return { getPrivatePlacements, reloadPrivatePlacements };
}
